import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import axios from "./axios";
import config from "../config";

const TICK_INTERVAL_MS = 1000;
const HEARTBEAT_INTERVAL_MS = 30000;
const MAX_TICK_MS = 2000;
const LOCK_TTL_MS = 8000;
const LOCK_KEY = "dh.sessionActivity.lock";
const CLIENT_SESSION_ID_KEY = "dh.sessionActivity.clientSessionId";

const createSessionId = () => `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const getStoredClientSessionId = () => {
  try {
    const existingClientSessionId = window.sessionStorage.getItem(CLIENT_SESSION_ID_KEY);
    if (existingClientSessionId) {
      return existingClientSessionId;
    }

    const nextClientSessionId = createSessionId();
    window.sessionStorage.setItem(CLIENT_SESSION_ID_KEY, nextClientSessionId);
    return nextClientSessionId;
  } catch (_) {
    return createSessionId();
  }
};

const clearStoredClientSessionId = () => {
  try {
    window.sessionStorage.removeItem(CLIENT_SESSION_ID_KEY);
  } catch (_) {
    // Ignore storage failures; the server-side logout heartbeat is the source of truth.
  }
};

const tabId = createSessionId();
const clientSessionId = getStoredClientSessionId();

let currentPathname = window.location.pathname;
let pendingMsByDate = {};
let tickTimer = null;
let heartbeatTimer = null;
let lastTickAt = Date.now();
let started = false;
let flushInFlight = false;
let flushPromise = null;

const getApiBaseUrl = () =>
  config.api || process.env.REACT_APP_API_URL || "http://localhost:4000/api";

const getClientSessionId = () => clientSessionId;

const getUserId = () => localStorage.getItem("user.id") || "";

const hasUserSession = () =>
  Boolean(localStorage.getItem("user.token") && localStorage.getItem("user.id"));

const normalizePathname = (pathname) => pathname.replace(/\/+$/, "") || "/";

const isTrackablePath = () => {
  const pathname = normalizePathname(currentPathname);

  if (pathname === "/" || pathname === "/login" || pathname === "/logout") {
    return false;
  }

  if (
    pathname.startsWith("/careers") ||
    pathname.startsWith("/consultant") ||
    pathname.startsWith("/resetpassword") ||
    pathname.startsWith("/user/resetPassword") ||
    pathname.startsWith("/userresetpassword") ||
    pathname.startsWith("/language-confidence")
  ) {
    return false;
  }

  return true;
};

const isVisibleAppTab = () => document.visibilityState === "visible";

const getDateKey = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const readLock = () => {
  try {
    const rawLock = localStorage.getItem(LOCK_KEY);
    return rawLock ? JSON.parse(rawLock) : null;
  } catch (_) {
    return null;
  }
};

const writeLock = () => {
  const userId = getUserId();
  if (!userId) return;

  localStorage.setItem(
    LOCK_KEY,
    JSON.stringify({
      tabId,
      userId,
      clientSessionId: getClientSessionId(),
      expiresAt: Date.now() + LOCK_TTL_MS,
    })
  );
};

const releaseLock = () => {
  const lock = readLock();
  if (lock?.tabId === tabId) {
    localStorage.removeItem(LOCK_KEY);
  }
};

const canTrackNow = () => hasUserSession() && isTrackablePath() && isVisibleAppTab();

const ensureTabLock = () => {
  if (!canTrackNow()) {
    releaseLock();
    return false;
  }

  writeLock();
  return true;
};

const addPendingTime = (deltaMs) => {
  const sessionDate = getDateKey();
  pendingMsByDate[sessionDate] = (pendingMsByDate[sessionDate] || 0) + deltaMs;
};

const takePendingEntries = () => {
  const entries = [];

  Object.keys(pendingMsByDate).forEach((sessionDate) => {
    const activeSeconds = Math.floor(pendingMsByDate[sessionDate] / 1000);

    if (activeSeconds > 0) {
      entries.push({ sessionDate, activeSeconds });
      pendingMsByDate[sessionDate] -= activeSeconds * 1000;
    }

    if (pendingMsByDate[sessionDate] <= 0) {
      delete pendingMsByDate[sessionDate];
    }
  });

  return entries;
};

const restorePendingEntries = (entries) => {
  entries.forEach(({ sessionDate, activeSeconds }) => {
    pendingMsByDate[sessionDate] =
      (pendingMsByDate[sessionDate] || 0) + activeSeconds * 1000;
  });
};

const captureCurrentActivity = () => {
  const now = Date.now();
  const deltaMs = Math.min(Math.max(now - lastTickAt, 0), MAX_TICK_MS);
  lastTickAt = now;

  if (deltaMs > 0 && ensureTabLock()) {
    addPendingTime(deltaMs);
  }
};

export const flushSessionActivity = ({
  endSession = false,
  keepalive = false,
  isActive = canTrackNow(),
} = {}) => {
  if (!hasUserSession()) {
    return Promise.resolve(false);
  }

  if (flushInFlight && !keepalive) {
    return flushPromise || Promise.resolve(false);
  }

  captureCurrentActivity();

  const entries = takePendingEntries();

  if (!entries.length && !endSession && typeof isActive !== "boolean") {
    return Promise.resolve(false);
  }

  const token = localStorage.getItem("user.token");
  const payload = {
    clientSessionId: getClientSessionId(),
    entries,
    ended: endSession,
    isActive: endSession ? false : isActive,
  };

  if (keepalive) {
    return fetch(`${getApiBaseUrl()}/user/session-activity/heartbeat`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => false);
  }

  flushInFlight = true;
  flushPromise = axios
    .post("/user/session-activity/heartbeat", payload, { tokenType: "user" })
    .then(() => true)
    .catch(() => {
      restorePendingEntries(entries);
      return false;
    })
    .finally(() => {
      flushInFlight = false;
      flushPromise = null;
    });

  return flushPromise;
};

const tick = () => {
  captureCurrentActivity();
};

const handleVisibilityOrFocusChange = () => {
  if (canTrackNow()) {
    flushSessionActivity({ isActive: true });
    return;
  }

  captureCurrentActivity();
  releaseLock();
  flushSessionActivity({ isActive: false, keepalive: true });
};

const handlePageExit = () => {
  captureCurrentActivity();
  flushSessionActivity({ keepalive: true, isActive: true });
  releaseLock();
};

const handleStorageChange = (event) => {
  if (event.key === "user.token" || event.key === "user.id") {
    if (!hasUserSession()) {
      releaseLock();
      pendingMsByDate = {};
    }
  }
};

export const startSessionActivityTracking = () => {
  if (started) return;

  started = true;
  lastTickAt = Date.now();

  tickTimer = window.setInterval(tick, TICK_INTERVAL_MS);
  heartbeatTimer = window.setInterval(() => flushSessionActivity(), HEARTBEAT_INTERVAL_MS);

  document.addEventListener("visibilitychange", handleVisibilityOrFocusChange);
  window.addEventListener("focus", handleVisibilityOrFocusChange);
  window.addEventListener("blur", handleVisibilityOrFocusChange);
  window.addEventListener("pagehide", handlePageExit);
  window.addEventListener("beforeunload", handlePageExit);
  window.addEventListener("storage", handleStorageChange);

  handleVisibilityOrFocusChange();
};

export const stopSessionActivityTracking = () => {
  if (!started) return;

  started = false;
  window.clearInterval(tickTimer);
  window.clearInterval(heartbeatTimer);
  tickTimer = null;
  heartbeatTimer = null;

  document.removeEventListener("visibilitychange", handleVisibilityOrFocusChange);
  window.removeEventListener("focus", handleVisibilityOrFocusChange);
  window.removeEventListener("blur", handleVisibilityOrFocusChange);
  window.removeEventListener("pagehide", handlePageExit);
  window.removeEventListener("beforeunload", handlePageExit);
  window.removeEventListener("storage", handleStorageChange);

  flushSessionActivity({ keepalive: true, isActive: true });
  releaseLock();
};

export const endSessionActivityTracking = () => {
  releaseLock();
  const flushResult = flushSessionActivity({
    endSession: true,
    keepalive: true,
    isActive: false,
  });
  clearStoredClientSessionId();
  return flushResult;
};

const SessionActivityTracker = () => {
  const location = useLocation();

  useEffect(() => {
    startSessionActivityTracking();
    return () => stopSessionActivityTracking();
  }, []);

  useEffect(() => {
    currentPathname = location.pathname;
    handleVisibilityOrFocusChange();
  }, [location.pathname]);

  return null;
};

export default SessionActivityTracker;
